import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Check, Loader2, Lock } from "lucide-react";
import { usePublicDocEdit } from "@/hooks/documents";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const parseApiError = (err: unknown): { code?: string; message?: string } => {
  const e = err as { apiError?: { code?: string; message?: string }; message?: string };
  return { code: e?.apiError?.code, message: e?.apiError?.message || e?.message };
};

const BrandMark = () => (
  <div className="flex items-center justify-center gap-2">
    <GenieIcon name="genie-lamp" size={28} className="h-7 w-7" />
    <span className="text-lg font-semibold text-[#222B45]">Help Genie</span>
  </div>
);

/**
 * Public, no-login page for a document "magic edit link" (/update/:token).
 * A business bookmarks this URL and updates one document — and every Genie that
 * uses it — without ever signing into the platform.
 */
const PublicDocumentEditPage = () => {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, isError, save } = usePublicDocEdit(token);

  const [content, setContent] = useState("");
  const [pin, setPin] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const original = data?.content ?? "";

  useEffect(() => {
    if (data) setContent(data.content ?? "");
  }, [data]);

  const isDirty = content !== original;
  const pinReady = !data?.requiresPin || pin.trim().length >= 4;
  const canSave = isDirty && pinReady && !save.isPending;

  const subtitle = useMemo(() => {
    if (!data) return "";
    if (data.agentNames.length === 1) return `Powering ${data.agentNames[0]}`;
    if (data.agentNames.length > 1) return `Powering ${data.agentNames.length} of your Genies`;
    return "Knowledge base document";
  }, [data]);

  const handleSave = async () => {
    setErrorMsg(null);
    if (data?.requiresPin && pin.trim().length < 4) {
      setErrorMsg("Enter your PIN to save.");
      return;
    }
    try {
      const result = await save.mutateAsync({ content, pin: data?.requiresPin ? pin.trim() : undefined });
      if (result.success) {
        setSavedAt(result.updatedAt ?? new Date().toISOString());
      } else {
        setErrorMsg("We couldn't save your update. Please try again.");
      }
    } catch (err) {
      const { code, message } = parseApiError(err);
      if (code === "RATE_LIMIT_EXCEEDED") {
        setErrorMsg(message || "Too many incorrect PIN attempts. Please try again later.");
      } else if (code === "FORBIDDEN") {
        setErrorMsg(message || "Incorrect PIN. Please try again.");
      } else {
        setErrorMsg(message || "We couldn't save your update. Please try again.");
      }
    }
  };

  // ---------- Loading ----------
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-genie-primary" />
        <p className="text-sm text-[#64748B]">Opening your document…</p>
      </div>
    );
  }

  // ---------- Invalid / revoked link ----------
  if (isError || !data) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-lg border border-[#E5EAF0]">
            <Lock className="h-7 w-7 text-genie-secondary" />
          </div>
          <h1 className="text-2xl font-bold text-[#222B45] mb-2">This link isn't active</h1>
          <p className="text-sm text-[#64748B]">
            This edit link is no longer available. It may have been revoked or replaced. Please ask the
            document owner for an up-to-date link.
          </p>
          <div className="mt-8">
            <BrandMark />
          </div>
        </div>
      </div>
    );
  }

  // ---------- Saved (magical confirmation) ----------
  if (savedAt) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-lg text-center">
          <div className="p-[2px] rounded-3xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] inline-block mb-8 shadow-xl shadow-genie-primary/20">
            <div className="rounded-[22px] bg-white px-10 py-12">
              <div
                className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full text-white shadow-lg"
                style={{ background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)" }}
              >
                <Check className="h-10 w-10" strokeWidth={3} />
              </div>
              <h1 className="text-2xl font-bold text-[#222B45] mb-2">Your Genie just got smarter</h1>
              <p className="text-sm text-[#64748B] max-w-sm mx-auto">
                Your update to <span className="font-semibold text-gray-700">{data.documentName}</span> is
                live. Anyone who talks to your Genie will hear the latest.
              </p>
              <button
                onClick={() => setSavedAt(null)}
                className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-genie-primary hover:underline"
              >
                <ArrowLeft className="h-4 w-4" />
                Make another change
              </button>
            </div>
          </div>
          <BrandMark />
        </div>
      </div>
    );
  }

  // ---------- Editor ----------
  return (
    <div className="min-h-screen bg-[#F9FAFC] overflow-x-hidden">
      <div className="h-1.5 w-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
      <div className="container mx-auto max-w-3xl px-4 py-8 md:py-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <BrandMark />
          <span className="text-xs text-[#64748B]">Live edit</span>
        </div>

        <div className="mb-6 flex items-start gap-3">
          <GenieIcon name="files" size={32} className="h-8 w-8 mt-0.5" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#222B45] leading-tight">
              {data.documentName}
            </h1>
            <p className="text-sm text-[#64748B] mt-0.5">{subtitle}</p>
          </div>
        </div>

        {/* Editor card with signature gradient border */}
        <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/10">
          <div className="rounded-[14px] bg-white p-4 md:p-6">
            <label className="text-sm font-semibold text-gray-700 mb-2 block">Document content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              spellCheck
              className="w-full h-[52vh] min-h-[320px] px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-sm font-mono resize-none leading-relaxed"
              placeholder="Update your document content…"
            />

            <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span>{content.length.toLocaleString()} characters</span>
              <span>{isDirty ? "Unsaved changes" : "No changes"}</span>
            </div>

            {/* PIN gate */}
            {data.requiresPin && (
              <div className="mt-5 rounded-xl border border-[#E5EAF0] bg-[#F9FAFC] p-4">
                <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-genie-primary" />
                  Enter PIN to save
                </label>
                <input
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="••••"
                  className="w-40 px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base tracking-[0.4em] font-mono"
                />
              </div>
            )}

            {errorMsg && (
              <div className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex items-center justify-end gap-3">
              {isDirty && (
                <button
                  onClick={() => {
                    setContent(original);
                    setErrorMsg(null);
                  }}
                  disabled={save.isPending}
                  className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  Discard changes
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={!canSave}
                className="inline-flex items-center gap-2 px-7 py-3 text-white rounded-xl font-semibold shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90 hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
              >
                {save.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Save to my Genie
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-[#64748B]">
          Changes save instantly to your Genie. No login required.
        </p>
      </div>
    </div>
  );
};

export default PublicDocumentEditPage;
